/**
 * Demo tools - generate CSV packs, reset/delete productions, edit user email/phone.
 * Opened from the LP workbench profile menu (“Demo tools”).
 */

import React, { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LpWorkbenchShell from '../components/org/LpWorkbenchShell';
import PhoneDialNationalInput from '../applicant/components/ui/PhoneDialNationalInput';
import { splitDialNationalFromStored } from '../applicant/utils/splitDialNational';
import { orgAuthApi } from '../services/api';
import { getLoginPath, getOrgSlatePath } from '../utils/postLoginNavigation';
import { countryNameFromIso2 } from '../utils/countries';
import { productionRegisterDisplayUrl } from '../utils/lpProductionHelpers';
import '../styles/LpWorkbench.css';

function detailMessage(err, fallback) {
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  return fallback;
}

/** True once invites were sent or someone registered (demo has been run). */
function productionDemoHasBeenUsed(production) {
  return (
    Number(production?.invites_sent_count) > 0
    || Number(production?.registered_count) > 0
    || String(production?.workflow_status || '').toLowerCase() === 'active'
  );
}

/** Digits-only E.164 - strips spaces, (), -, etc. */
function toE164(countryCode, nationalRaw) {
  const digits = String(nationalRaw || '').replace(/\D/g, '');
  if (!digits) return '';
  const ccDigits = String(countryCode || '').replace(/\D/g, '') || '1';
  return `+${ccDigits}${digits}`;
}

/** Origin / incoming ISO2 codes for a production (excludes shoot destination). */
function collectIncomingCountries(detail, production) {
  const dest = String(
    detail?.project?.destination_country
    || production?.destination_country
    || '',
  ).trim().toUpperCase();
  const codes = [];
  const seen = new Set();

  function add(raw) {
    const code = String(raw || '').trim().toUpperCase();
    if (code.length !== 2 || seen.has(code)) return;
    if (dest && code === dest) return;
    seen.add(code);
    codes.push(code);
  }

  for (const person of detail?.participants || []) {
    add(person.country);
  }
  for (const c of detail?.countries || []) {
    add(c);
  }
  for (const c of production?.countries || []) {
    add(c);
  }
  add(detail?.project?.origin_country || production?.origin_country);

  if (!codes.length && dest) codes.push(dest);
  return codes.map((code) => ({
    code,
    label: countryNameFromIso2(code) || code,
  }));
}

function joinLinkDisplay(url) {
  return String(url || '').replace(/^https?:\/\//i, '');
}

function filenameFromDisposition(header, fallback) {
  if (!header || typeof header !== 'string') return fallback;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match?.[1]?.trim() || fallback;
}

async function triggerBlobDownload(response, fallbackName) {
  const blob = response.data;
  const name = filenameFromDisposition(
    response.headers?.['content-disposition'],
    fallbackName,
  );
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = name;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
}

/** Destination / origin countries we support for demo production data. */
const COUNTRY_OPTIONS = [
  { code: 'AR', label: 'Argentina (AR)' },
  { code: 'BR', label: 'Brazil (BR)' },
  { code: 'CL', label: 'Chile (CL)' },
  { code: 'CO', label: 'Colombia (CO)' },
  { code: 'CZ', label: 'Czech Republic (CZ)' },
  { code: 'FR', label: 'France (FR)' },
  { code: 'DE', label: 'Germany (DE)' },
  { code: 'HU', label: 'Hungary (HU)' },
  { code: 'IE', label: 'Ireland (IE)' },
  { code: 'IT', label: 'Italy (IT)' },
  { code: 'MX', label: 'Mexico (MX)' },
  { code: 'NL', label: 'Netherlands (NL)' },
  { code: 'PE', label: 'Peru (PE)' },
  { code: 'PT', label: 'Portugal (PT)' },
  { code: 'ES', label: 'Spain (ES)' },
  { code: 'GB', label: 'United Kingdom (GB)' },
  { code: 'US', label: 'United States (US)' },
  { code: 'UY', label: 'Uruguay (UY)' },
];

/** Countries offered in the New demo modal (design). */
const NEW_DEMO_COUNTRY_OPTIONS = [
  { code: 'US', label: 'United States' },
  { code: 'ES', label: 'Spain' },
  { code: 'IT', label: 'Italy' },
];

/** Default cast for new USA-focused demos (no domestic US crew for now). */
const NEW_DEMO_ROSTER = [
  { country: 'GB', name: 'James Whitfield' },
  { country: 'CO', name: 'Camila Restrepo' },
  { country: 'CO', name: 'Andrés Morales' },
  { country: 'IN', name: 'Priya Sharma' },
  { country: 'IN', name: 'Rohan Mehta' },
  { country: 'IN', name: 'Ananya Iyer' },
];

const DEMO_ACTOR_ROLE = 'Actor';

/** ISO date YYYY-MM-DD in local calendar. */
function toIsoDateLocal(d) {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Demo shoot window: start = today + 5 months, length = 3 weeks.
 * Returns 2–3 travel dates within the first 2/3 of that window (Needed by).
 */
function demoShootTravelPlan(now = new Date()) {
  const start = new Date(now.getFullYear(), now.getMonth() + 5, now.getDate());
  const shootDays = 21;
  const end = new Date(start);
  end.setDate(end.getDate() + shootDays);
  const maxOffset = Math.floor((shootDays * 2) / 3); // inclusive last day ≤ 2/3 through
  const count = 2 + Math.floor(Math.random() * 2); // 2 or 3
  const offsets = new Set();
  while (offsets.size < count) {
    offsets.add(Math.floor(Math.random() * (maxOffset + 1)));
  }
  const travelDates = [...offsets]
    .sort((a, b) => a - b)
    .map((off) => {
      const d = new Date(start);
      d.setDate(d.getDate() + off);
      return toIsoDateLocal(d);
    });
  return {
    scheduleEnd: toIsoDateLocal(end),
    travelDates,
  };
}

function demoRosterEmail(productionName, personName, index) {
  const slug = String(productionName || 'demo')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 24) || 'demo';
  const local = String(personName || `guest${index}`)
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '.')
    .replace(/^\.+|\.+$/g, '')
    .slice(0, 32) || `guest${index}`;
  return `${local}.${index + 1}@${slug}.demo`;
}

const FILE_KINDS = [
  { kind: 'roster', label: 'Roster' },
  { kind: 'budget', label: 'Budget' },
  { kind: 'schedule', label: 'Schedule' },
  { kind: 'day_out_of_days', label: 'Day out of days' },
];

export default function OrgAdminTools() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [activeTab] = useState('productions');
  const [genFieldErrors, setGenFieldErrors] = useState({});
  const [newDemoOpen, setNewDemoOpen] = useState(false);
  const [creatingDemo, setCreatingDemo] = useState(false);
  const [newDemoError, setNewDemoError] = useState('');

  const [productions, setProductions] = useState([]);
  const [prodLoading, setProdLoading] = useState(true);
  const [prodError, setProdError] = useState('');
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState('');
  const [deleteMemberTarget, setDeleteMemberTarget] = useState(null);
  const [deletingMember, setDeletingMember] = useState(false);
  const [deleteMemberError, setDeleteMemberError] = useState('');
  const [addMemberProductionId, setAddMemberProductionId] = useState(null);
  const [addMemberForm, setAddMemberForm] = useState({
    name: '',
    email: '',
    phoneCountryCode: '+1',
    phoneNational: '',
    job_title: '',
    country: 'US',
  });
  const [addingMember, setAddingMember] = useState(false);
  const [addMemberError, setAddMemberError] = useState('');
  const [addMemberMessage, setAddMemberMessage] = useState('');
  const [resetTarget, setResetTarget] = useState(null);
  const [resetting, setResetting] = useState(false);
  const [resetError, setResetError] = useState('');
  const [resetMessage, setResetMessage] = useState('');
  const [demoHost, setDemoHost] = useState({ name: '', email: '', country: '' });
  const [audienceEmails, setAudienceEmails] = useState([]);
  const [demoGuestCountries, setDemoGuestCountries] = useState([]);
  const [demoJoinUrl, setDemoJoinUrl] = useState('');
  const [demoJoinCopied, setDemoJoinCopied] = useState(false);
  const [demoCountriesLoading, setDemoCountriesLoading] = useState(false);

  const [genName, setGenName] = useState('');
  const [genStart, setGenStart] = useState('');
  const [genEnd, setGenEnd] = useState('');
  const [genDest, setGenDest] = useState('US');
  const [genOrigin, setGenOrigin] = useState('US');
  const [genTotal, setGenTotal] = useState(1);
  const [generating, setGenerating] = useState(false);
  const [genError, setGenError] = useState('');
  const [genMessage, setGenMessage] = useState('');
  const [generatedPacks, setGeneratedPacks] = useState([]);
  const [packsLoading, setPacksLoading] = useState(true);
  const [downloadingKey, setDownloadingKey] = useState(null);
  const [itineraryDownloadingId, setItineraryDownloadingId] = useState(null);

  /** Expanded production roster under Open */
  const [openProductionId, setOpenProductionId] = useState(null);
  const [rosterByProduction, setRosterByProduction] = useState({});
  const [rosterLoadingId, setRosterLoadingId] = useState(null);
  const [rosterError, setRosterError] = useState('');

  const [query, setQuery] = useState('');
  const [users, setUsers] = useState([]);
  const [userLoading, setUserLoading] = useState(false);
  const [userError, setUserError] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  /** When editing from a production roster, scope uniqueness + invite sync to this project. */
  const [editingProjectId, setEditingProjectId] = useState(null);
  /** Invite/participant id when editing from roster (works before registration). */
  const [editingInviteId, setEditingInviteId] = useState(null);
  const [emailDraft, setEmailDraft] = useState('');
  const [phoneCountryCode, setPhoneCountryCode] = useState('+1');
  const [phoneNational, setPhoneNational] = useState('');
  const [savingContact, setSavingContact] = useState(false);
  const [contactMessage, setContactMessage] = useState('');
  const [contactError, setContactError] = useState('');

  const genFormReady = Boolean(
    String(genName || '').trim()
    && genStart
    && genEnd
    && genEnd >= genStart
    && genOrigin
    && genDest
    && Number(genTotal) >= 1,
  );

  const loadProductions = useCallback(async () => {
    setProdLoading(true);
    setProdError('');
    try {
      const res = await orgAuthApi.listProjects();
      setProductions(res.data?.projects || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setProdError(detailMessage(err, 'Could not load productions.'));
    } finally {
      setProdLoading(false);
    }
  }, [navigate]);

  const loadGeneratedPacks = useCallback(async () => {
    setPacksLoading(true);
    try {
      const res = await orgAuthApi.listGeneratedPacks();
      setGeneratedPacks(res.data?.packs || []);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setGeneratedPacks([]);
    } finally {
      setPacksLoading(false);
    }
  }, [navigate]);

  useEffect(() => {
    loadProductions();
    loadGeneratedPacks();
  }, [loadProductions, loadGeneratedPacks]);

  async function downloadGenerated(packId, kind) {
    const key = kind ? `${packId}:${kind}` : `${packId}:zip`;
    if (downloadingKey) return;
    setDownloadingKey(key);
    setGenError('');
    try {
      if (!kind) {
        const res = await orgAuthApi.downloadGeneratedPackZip(packId);
        await triggerBlobDownload(res, `${packId}_files.zip`);
      } else {
        const res = await orgAuthApi.downloadGeneratedPackFile(packId, kind);
        const label = FILE_KINDS.find((f) => f.kind === kind)?.label || kind;
        await triggerBlobDownload(res, `${label}.csv`);
      }
    } catch (err) {
      setGenError(detailMessage(err, 'Could not download generated file.'));
    } finally {
      setDownloadingKey(null);
    }
  }

  function validateGenerateForm() {
    const errors = {};
    if (!String(genName || '').trim()) {
      errors.name = 'Production name is required.';
    }
    if (!genStart) {
      errors.start = 'Production begin date is required.';
    }
    if (!genEnd) {
      errors.end = 'Production end date is required.';
    }
    if (genStart && genEnd && genEnd < genStart) {
      errors.end = 'End date must be on or after the start date.';
    }
    if (!genOrigin) {
      errors.origin = 'Coming from is required.';
    }
    if (!genDest) {
      errors.dest = 'Production country is required.';
    }
    const total = Number(genTotal);
    if (!genTotal || Number.isNaN(total) || total < 1) {
      errors.total = 'Total participants must be at least 1.';
    }
    return errors;
  }

  const newDemoReady = Boolean(String(genName || '').trim() && genDest);

  function openNewDemo() {
    setNewDemoError('');
    setGenFieldErrors({});
    setNewDemoOpen(true);
  }

  function closeNewDemo() {
    if (creatingDemo) return;
    setNewDemoOpen(false);
    setNewDemoError('');
    setGenFieldErrors({});
  }

  async function submitNewDemo(e) {
    e?.preventDefault?.();
    if (creatingDemo || !newDemoReady) return;
    const name = String(genName || '').trim();
    const dest = String(genDest || '').trim().toUpperCase();
    const errors = {};
    if (!name) errors.name = 'Production name is required.';
    if (!dest) errors.dest = 'Country is required.';
    setGenFieldErrors(errors);
    if (Object.keys(errors).length) {
      setNewDemoError('Fill in all required fields.');
      return;
    }
    setCreatingDemo(true);
    setNewDemoError('');
    try {
      const res = await orgAuthApi.createProject({
        name,
        destination_country: dest,
        industry: 'film_tv',
      });
      const productionId = res.data?.project?.id;
      if (productionId && dest === 'US') {
        const { scheduleEnd, travelDates } = demoShootTravelPlan();
        for (let i = 0; i < NEW_DEMO_ROSTER.length; i += 1) {
          const person = NEW_DEMO_ROSTER[i];
          await orgAuthApi.addProductionParticipant(productionId, {
            name: person.name,
            email: demoRosterEmail(name, person.name, i),
            country: person.country,
            job_title: DEMO_ACTOR_ROLE,
            department: 'Cast',
            schedule_start_date: travelDates[i % travelDates.length],
            schedule_end_date: scheduleEnd,
            send_invite: false,
            upsert_if_email_exists: true,
          });
        }
      }
      setGenName('');
      setGenDest('US');
      setGenFieldErrors({});
      setNewDemoOpen(false);
      setResetMessage(
        dest === 'US'
          ? `Created demo "${name}" with 6 cast members (1 UK, 2 Colombia, 3 India).`
          : `Created demo "${name}".`,
      );
      await loadProductions();
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setNewDemoError(detailMessage(err, t('lpWorkbench.createDemoFailed')));
    } finally {
      setCreatingDemo(false);
    }
  }

  async function submitGeneratePack(e) {
    e?.preventDefault?.();
    if (generating || !genFormReady) return;
    const errors = validateGenerateForm();
    setGenFieldErrors(errors);
    if (Object.keys(errors).length) {
      setGenError('Fill in all required fields.');
      setGenMessage('');
      return;
    }
    setGenerating(true);
    setGenError('');
    setGenMessage('');
    const nameSnapshot = genName.trim();
    const totalSnapshot = Math.max(1, Number(genTotal) || 1);
    try {
      const res = await orgAuthApi.generateProductionPack({
        production_name: nameSnapshot,
        start_date: genStart,
        end_date: genEnd,
        production_country: genDest,
        origin_countries: [{ country: genOrigin, count: totalSnapshot }],
        total_participants: totalSnapshot,
      });
      setGenMessage(
        `Created demo for "${res.data?.pack?.production_name || nameSnapshot}" with ${res.data?.pack?.total_participants || totalSnapshot} people.`,
      );
      setGenFieldErrors({});
      setGenName('');
      setGenStart('');
      setGenEnd('');
      setGenDest('ES');
      setGenOrigin('US');
      setGenTotal(1);
      await loadGeneratedPacks();
    } catch (err) {
      setGenError(detailMessage(err, 'Could not create demo files.'));
    } finally {
      setGenerating(false);
    }
  }

  async function downloadParticipantItinerary(productionId, participant) {
    if (itineraryDownloadingId) return;
    setItineraryDownloadingId(participant.id);
    setRosterError('');
    try {
      const res = await orgAuthApi.downloadParticipantTravelItinerary(productionId, participant.id);
      await triggerBlobDownload(
        res,
        `travel_itinerary_${participant.name || participant.id}.csv`,
      );
    } catch (err) {
      setRosterError(detailMessage(err, 'Could not create travel itinerary CSV.'));
    } finally {
      setItineraryDownloadingId(null);
    }
  }

  async function searchUsers(e) {
    e?.preventDefault?.();
    setUserLoading(true);
    setUserError('');
    setContactMessage('');
    setContactError('');
    try {
      const res = await orgAuthApi.listOrganizationUsers({
        q: query.trim() || undefined,
        page: 1,
        page_size: 50,
      });
      setUsers(res.data?.users || []);
      if (!(res.data?.users || []).length) {
        setUserError('No users matched that search.');
      }
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setUsers([]);
      setUserError(detailMessage(err, 'Could not search users.'));
    } finally {
      setUserLoading(false);
    }
  }

  function clearContactEditor() {
    setSelectedUser(null);
    setEditingProjectId(null);
    setEditingInviteId(null);
    setContactMessage('');
    setContactError('');
  }

  function selectUser(user, projectId = null, inviteId = null) {
    setSelectedUser(user);
    setEditingProjectId(projectId != null ? Number(projectId) : null);
    setEditingInviteId(inviteId != null ? Number(inviteId) : null);
    setEmailDraft(user.email || '');
    const split = splitDialNationalFromStored(user.phone || '', '+1');
    setPhoneCountryCode(split.dial || '+1');
    setPhoneNational(String(split.national || '').replace(/\D/g, ''));
    setContactMessage('');
    setContactError('');
  }

  async function toggleProductionRoster(production) {
    const id = production.id;
    if (openProductionId === id) {
      setOpenProductionId(null);
      setRosterError('');
      setAddMemberProductionId(null);
      setAddMemberError('');
      setAddMemberMessage('');
      if (Number(editingProjectId) === Number(id)) clearContactEditor();
      return;
    }
    setOpenProductionId(id);
    setRosterError('');
    setAddMemberProductionId(null);
    setAddMemberError('');
    setAddMemberMessage('');
    if (rosterByProduction[id]) return;

    setRosterLoadingId(id);
    try {
      const res = await orgAuthApi.getProduction(id);
      const participants = res.data?.participants || [];
      setRosterByProduction((prev) => ({ ...prev, [id]: participants }));
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setRosterError(detailMessage(err, 'Could not load people for this production.'));
      setOpenProductionId(null);
    } finally {
      setRosterLoadingId(null);
    }
  }

  async function refreshProductionRoster(productionId) {
    const id = Number(productionId);
    setRosterLoadingId(id);
    try {
      const res = await orgAuthApi.getProduction(id);
      const participants = res.data?.participants || [];
      setRosterByProduction((prev) => ({ ...prev, [id]: participants }));
      setProductions((prev) =>
        prev.map((p) =>
          Number(p.id) === id
            ? { ...p, participant_count: participants.length }
            : p,
        ),
      );
    } catch (err) {
      setRosterError(detailMessage(err, 'Could not refresh people for this production.'));
    } finally {
      setRosterLoadingId(null);
    }
  }

  function openAddMemberForm(productionId) {
    setAddMemberProductionId(productionId);
    setAddMemberForm({
      name: '',
      email: '',
      phoneCountryCode: '+1',
      phoneNational: '',
      job_title: '',
      country: 'US',
    });
    setAddMemberError('');
    setAddMemberMessage('');
  }

  async function submitAddMember(productionId) {
    if (addingMember) return;
    const email = (addMemberForm.email || '').trim();
    if (!email) {
      setAddMemberError('Email is required.');
      return;
    }
    setAddingMember(true);
    setAddMemberError('');
    setAddMemberMessage('');
    try {
      const phoneE164 = toE164(addMemberForm.phoneCountryCode, addMemberForm.phoneNational);
      await orgAuthApi.addProductionParticipant(productionId, {
        name: (addMemberForm.name || '').trim() || undefined,
        email,
        phone: phoneE164 || undefined,
        job_title: (addMemberForm.job_title || '').trim() || undefined,
        country: (addMemberForm.country || '').trim() || undefined,
        send_invite: false,
      });
      setAddMemberMessage('Member added to this production.');
      setAddMemberForm({
        name: '',
        email: '',
        phoneCountryCode: addMemberForm.phoneCountryCode || '+1',
        phoneNational: '',
        job_title: '',
        country: addMemberForm.country || 'US',
      });
      await refreshProductionRoster(productionId);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setAddMemberError(detailMessage(err, 'Could not add this member.'));
    } finally {
      setAddingMember(false);
    }
  }

  async function confirmDeleteMember() {
    if (!deleteMemberTarget || deletingMember) return;
    const { production, person } = deleteMemberTarget;
    setDeletingMember(true);
    setDeleteMemberError('');
    try {
      await orgAuthApi.deleteProductionParticipant(production.id, person.id);
      if (
        editingInviteId != null
        && Number(editingInviteId) === Number(person.id)
      ) {
        setSelectedUser(null);
        setEditingProjectId(null);
        setEditingInviteId(null);
        setEmailDraft('');
        setPhoneCountryCode('+1');
        setPhoneNational('');
        setContactMessage('');
        setContactError('');
      }
      setDeleteMemberTarget(null);
      await refreshProductionRoster(production.id);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      setDeleteMemberError(detailMessage(err, 'Could not remove this member.'));
    } finally {
      setDeletingMember(false);
    }
  }

  async function editParticipant(production, participant) {
    // Toggle inline editor under this roster row (stay on Productions tab).
    if (
      selectedUser
      && Number(editingInviteId) === Number(participant.id)
      && Number(editingProjectId) === Number(production.id)
    ) {
      clearContactEditor();
      return;
    }

    setContactError('');
    setContactMessage('');

    const userId = participant.registered_user_id || null;
    let phone = participant.phone || '';
    let name = participant.name || null;
    let email = participant.email || '';

    // If already registered, enrich from org users search when possible.
    if (userId) {
      try {
        const res = await orgAuthApi.listOrganizationUsers({
          q: String(userId),
          page: 1,
          page_size: 5,
        });
        const match = (res.data?.users || []).find((u) => Number(u.id) === Number(userId));
        if (match) {
          phone = phone || match.phone || '';
          name = name || match.name || null;
          email = email || match.email || '';
        }
      } catch {
        // Roster fields are enough to open the editor.
      }
    }

    selectUser(
      {
        id: userId || participant.id,
        registered_user_id: userId,
        name,
        email,
        phone,
        role: 'user',
      },
      production.id,
      participant.id,
    );
  }

  function renderContactEditor({ inline = false } = {}) {
    if (!selectedUser) return null;
    return (
      <div className={`lp-admin-edit${inline ? ' lp-admin-edit--inline' : ''}`}>
        {!inline ? (
          <div className="lp-admin-row-title" style={{ marginBottom: 12 }}>
            Editing {selectedUser.name || selectedUser.email || `participant #${selectedUser.id}`}
            {editingInviteId != null && !selectedUser.registered_user_id ? ' (invite)' : ''}
          </div>
        ) : null}
        <label className="lp-admin-label">
          Email
          <div className="lp-admin-field-row">
            <input
              type="email"
              className="lp-admin-input"
              value={emailDraft}
              onChange={(e) => setEmailDraft(e.target.value)}
              placeholder={editingInviteId != null ? 'Required for invite' : 'Clear to remove'}
              autoComplete="off"
            />
            {editingInviteId == null ? (
              <button
                type="button"
                className="lp-bn-btn ghost"
                disabled={savingContact || !emailDraft}
                onClick={() => {
                  setEmailDraft('');
                  saveContact({
                    email: '',
                    phone: toE164(phoneCountryCode, phoneNational),
                  });
                }}
              >
                Clear
              </button>
            ) : null}
          </div>
        </label>
        <label className="lp-admin-label">
          Phone
          <div className="lp-admin-field-row">
            <div style={{ flex: 1, minWidth: 0 }}>
              <PhoneDialNationalInput
                countryCode={phoneCountryCode || '+1'}
                onCountryCodeChange={setPhoneCountryCode}
                nationalNumber={phoneNational}
                onNationalNumberChange={(value) =>
                  setPhoneNational(String(value || '').replace(/\D/g, ''))
                }
                placeholder="National number"
                theme="light"
              />
            </div>
            <button
              type="button"
              className="lp-bn-btn ghost"
              disabled={savingContact || !phoneNational}
              onClick={() => {
                setPhoneNational('');
                saveContact({ email: emailDraft, phone: '' });
              }}
            >
              Clear
            </button>
          </div>
        </label>
        {contactError ? (
          <div className="lp-delete-err" role="alert">{contactError}</div>
        ) : null}
        {contactMessage ? (
          <div className="lp-admin-ok" role="status">{contactMessage}</div>
        ) : null}
        <div className="lp-modal-actions" style={{ marginTop: 14 }}>
          <button
            type="button"
            className="lp-bn-btn ghost"
            onClick={clearContactEditor}
            disabled={savingContact}
          >
            Cancel
          </button>
          <button
            type="button"
            className="lp-bn-btn"
            onClick={() => saveContact()}
            disabled={savingContact}
          >
            {savingContact ? 'Saving…' : 'Save contact'}
          </button>
        </div>
      </div>
    );
  }

  async function saveContact(overrides = {}) {
    if (!selectedUser || savingContact) return;
    const email = Object.prototype.hasOwnProperty.call(overrides, 'email')
      ? overrides.email
      : emailDraft;
    const phone = Object.prototype.hasOwnProperty.call(overrides, 'phone')
      ? overrides.phone
      : toE164(phoneCountryCode, phoneNational);

    if (editingInviteId != null && !(email || '').trim()) {
      setContactError('Invite email cannot be empty.');
      return;
    }

    setSavingContact(true);
    setContactMessage('');
    setContactError('');
    try {
      let updatedEmail = email;
      let updatedPhone = phone;
      let updatedName = selectedUser.name;

      if (editingInviteId != null && editingProjectId != null) {
        const res = await orgAuthApi.updateParticipantContact(
          editingProjectId,
          editingInviteId,
          { email, phone },
        );
        const participant = res.data?.participant;
        updatedEmail = participant?.email ?? email;
        updatedPhone = participant?.phone ?? (phone === '' ? null : phone);
        updatedName = participant?.name || updatedName;
        setSelectedUser((prev) => ({
          ...prev,
          email: updatedEmail || null,
          phone: updatedPhone || null,
          name: updatedName,
        }));
        setEmailDraft(updatedEmail || '');
        const split = splitDialNationalFromStored(updatedPhone || '', phoneCountryCode || '+1');
        setPhoneCountryCode(split.dial || '+1');
        setPhoneNational(String(split.national || '').replace(/\D/g, ''));
        setRosterByProduction((prev) => {
          const next = { ...prev };
          const key = editingProjectId;
          next[key] = (next[key] || []).map((p) => (
            Number(p.id) === Number(editingInviteId)
              ? {
                  ...p,
                  email: updatedEmail || p.email,
                  phone: updatedPhone,
                  name: updatedName || p.name,
                }
              : p
          ));
          return next;
        });
      } else {
        const payload = { email, phone };
        if (editingProjectId != null) {
          payload.organization_project_id = editingProjectId;
        }
        const res = await orgAuthApi.updateOrganizationUserContact(selectedUser.id, payload);
        const updated = res.data?.user || {
          ...selectedUser,
          email: email || null,
          phone: phone || null,
        };
        setSelectedUser(updated);
        setEmailDraft(updated.email || '');
        const split = splitDialNationalFromStored(updated.phone || '', phoneCountryCode || '+1');
        setPhoneCountryCode(split.dial || '+1');
        setPhoneNational(String(split.national || '').replace(/\D/g, ''));
        setUsers((prev) => prev.map((u) => (u.id === updated.id ? { ...u, ...updated } : u)));
        setRosterByProduction((prev) => {
          const next = { ...prev };
          Object.keys(next).forEach((prodId) => {
            const scoped = editingProjectId == null || Number(prodId) === Number(editingProjectId);
            if (!scoped) return;
            next[prodId] = (next[prodId] || []).map((p) => (
              Number(p.registered_user_id) === Number(updated.id)
                ? {
                    ...p,
                    email: (email !== undefined && email !== null ? email : p.email) || p.email,
                    phone: phone === '' ? null : (phone || p.phone),
                    name: updated.name || p.name,
                  }
                : p
            ));
          });
          return next;
        });
      }
      setContactMessage('Contact details saved.');
    } catch (err) {
      setContactError(detailMessage(err, 'Could not update contact details.'));
    } finally {
      setSavingContact(false);
    }
  }

  async function confirmDeleteProduction() {
    if (!deleteTarget || deleting) return;
    setDeleting(true);
    setDeleteError('');
    try {
      const deletedId = deleteTarget.id;
      await orgAuthApi.deleteProduction(deletedId);
      setDeleteTarget(null);
      if (openProductionId === deletedId) setOpenProductionId(null);
      setRosterByProduction((prev) => {
        const next = { ...prev };
        delete next[deletedId];
        return next;
      });
      await loadProductions();
    } catch (err) {
      setDeleteError(detailMessage(err, 'Could not delete this production.'));
    } finally {
      setDeleting(false);
    }
  }

  async function openStartDemo(production) {
    setResetTarget(production);
    setResetError('');
    setResetMessage('');
    setDemoJoinCopied(false);
    setDemoJoinUrl(
      productionRegisterDisplayUrl(
        production?.customer_signup_ref,
        production?.customer_signup_url,
      ),
    );
    setDemoGuestCountries([]);
    // Leave host blank — LP admin is already signed in and must not be added as a demo participant.
    setDemoHost({ name: '', email: '', country: '' });
    setAudienceEmails([]);
    setDemoCountriesLoading(true);
    try {
      const res = await orgAuthApi.getProduction(production.id);
      const project = res.data?.project;
      setDemoJoinUrl(
        productionRegisterDisplayUrl(
          project?.customer_signup_ref,
          project?.customer_signup_url,
        ),
      );
      const options = collectIncomingCountries(res.data, production);
      setDemoGuestCountries(options);
      setDemoHost((prev) => ({
        ...prev,
        country: options[0]?.code || '',
      }));
    } catch {
      const options = collectIncomingCountries(null, production);
      setDemoGuestCountries(options);
      setDemoHost((prev) => ({
        ...prev,
        country: options[0]?.code || '',
      }));
    } finally {
      setDemoCountriesLoading(false);
    }
  }

  function closeStartDemo() {
    if (resetting) return;
    setResetTarget(null);
    setResetError('');
    setDemoHost({ name: '', email: '', country: '' });
    setAudienceEmails([]);
    setDemoGuestCountries([]);
    setDemoJoinUrl('');
    setDemoJoinCopied(false);
  }

  function updateDemoHost(patch) {
    setDemoHost((prev) => ({ ...prev, ...patch }));
  }

  function updateAudienceEmail(index, value) {
    setAudienceEmails((prev) => prev.map((email, i) => (i === index ? value : email)));
  }

  function removeAudienceEmail(index) {
    setAudienceEmails((prev) => prev.filter((_, i) => i !== index));
  }

  async function copyDemoJoinLink() {
    if (!demoJoinUrl) return;
    try {
      await navigator.clipboard.writeText(demoJoinUrl);
      setDemoJoinCopied(true);
      setTimeout(() => setDemoJoinCopied(false), 2000);
    } catch {
      /* ignore */
    }
  }

  async function confirmResetProduction() {
    if (!resetTarget || resetting) return;
    const productionId = resetTarget.id;
    const fallbackCountry = demoGuestCountries[0]?.code || demoHost.country || 'US';
    const hostName = (demoHost.name || '').trim();
    const hostEmail = (demoHost.email || '').trim();
    const hostCountry = String(demoHost.country || '').trim().toUpperCase();
    const audience = audienceEmails.map((email) => email.trim()).filter(Boolean);

    if (hostEmail && !hostName) {
      setResetError(t('lpWorkbench.nameRequiredEach'));
      return;
    }
    if (hostEmail && demoGuestCountries.length && !hostCountry) {
      setResetError(t('lpWorkbench.chooseComingFrom'));
      return;
    }
    if (audience.some((email) => !email.includes('@'))) {
      setResetError(t('lpWorkbench.audienceEmailInvalid'));
      return;
    }

    const toCreate = [];
    if (hostEmail) {
      toCreate.push({
        name: hostName,
        email: hostEmail,
        country: hostCountry || fallbackCountry,
        job_title: DEMO_ACTOR_ROLE,
        department: 'Cast',
      });
    }
    audience.forEach((email) => {
      toCreate.push({
        name: email.split('@')[0] || email,
        email,
        country: hostCountry || fallbackCountry,
        job_title: DEMO_ACTOR_ROLE,
        department: 'Cast',
      });
    });

    setResetting(true);
    setResetError('');
    setResetMessage('');
    try {
      // Clear filled participant data, then wipe the roster so prior hosts /
      // audience from earlier demo runs do not linger.
      await orgAuthApi.resetProduction(productionId);

      const detail = await orgAuthApi.getProduction(productionId);
      const existing = detail.data?.participants || [];
      for (const person of existing) {
        try {
          await orgAuthApi.deleteProductionParticipant(productionId, person.id);
        } catch {
          /* keep wiping remaining rows */
        }
      }

      const destCode = String(
        resetTarget?.destination_country || '',
      ).trim().toUpperCase();
      const { scheduleEnd, travelDates } = demoShootTravelPlan();
      let travelIdx = 0;
      if (destCode === 'US') {
        const productionName = resetTarget?.name || 'demo';
        for (let i = 0; i < NEW_DEMO_ROSTER.length; i += 1) {
          const person = NEW_DEMO_ROSTER[i];
          await orgAuthApi.addProductionParticipant(productionId, {
            name: person.name,
            email: demoRosterEmail(productionName, person.name, i),
            country: person.country,
            job_title: DEMO_ACTOR_ROLE,
            department: 'Cast',
            schedule_start_date: travelDates[travelIdx % travelDates.length],
            schedule_end_date: scheduleEnd,
            send_invite: false,
            upsert_if_email_exists: true,
          });
          travelIdx += 1;
        }
      }

      for (const guest of toCreate) {
        await orgAuthApi.addProductionParticipant(productionId, {
          name: guest.name || undefined,
          email: guest.email,
          country: guest.country || fallbackCountry,
          job_title: guest.job_title || DEMO_ACTOR_ROLE,
          department: guest.department || 'Cast',
          schedule_start_date: travelDates[travelIdx % travelDates.length],
          schedule_end_date: scheduleEnd,
          send_invite: false,
          upsert_if_email_exists: true,
        });
        travelIdx += 1;
      }

      try {
        await orgAuthApi.sendProductionInvitations(productionId);
      } catch (err) {
        const msg = detailMessage(err, '');
        if (!/no pending invitations/i.test(msg)) {
          throw err;
        }
      }

      setResetTarget(null);
      setDemoHost({ name: '', email: '', country: '' });
      setAudienceEmails([]);
      setResetMessage(
        toCreate.length
          ? `Demo started — invited ${toCreate.length} participant${toCreate.length === 1 ? '' : 's'}.`
          : 'Demo restarted for this production.',
      );
      setRosterByProduction((prev) => {
        const next = { ...prev };
        delete next[productionId];
        return next;
      });
      if (openProductionId === productionId) setOpenProductionId(null);
      await loadProductions();
      navigate(`/org/productions/${productionId}`);
    } catch (err) {
      if (err?.response?.status === 401) {
        navigate(getLoginPath(), { replace: true });
        return;
      }
      const timedOut = err?.code === 'ECONNABORTED'
        || /timeout/i.test(String(err?.message || ''));
      setResetError(
        timedOut
          ? t('lpWorkbench.startDemoTimeout')
          : detailMessage(err, t('lpWorkbench.startDemoFailed')),
      );
    } finally {
      setResetting(false);
    }
  }

  return (
    <LpWorkbenchShell slateTheme hideLanguage>
      <div className="lp-admin-page lp-demo-tools-page">
        <button
          type="button"
          className="lp-demo-back"
          onClick={() => navigate(getOrgSlatePath())}
        >
          {t('lpWorkbench.backToProductions')}
        </button>
        <div className="lp-admin-header">
          <h1>{t('lpWorkbench.demoTools')}</h1>
          <button
            type="button"
            className="lp-bn-btn lp-demo-new-btn"
            onClick={openNewDemo}
          >
            {t('lpWorkbench.newDemoButton')}
          </button>
        </div>

        {activeTab === 'generate' ? (
        <section
          className="lp-admin-panel"
          id="lp-demo-panel-generate"
          role="tabpanel"
          aria-labelledby="lp-demo-tab-generate"
        >
          <h2 id="lp-admin-files-title" className="lp-admin-section-title">Create production</h2>

          <form className="lp-admin-gen-form" onSubmit={submitGeneratePack} noValidate>
            <label className="lp-admin-label">
              Production name
              <input
                className={`lp-admin-input${genFieldErrors.name ? ' is-invalid' : ''}`}
                value={genName}
                onChange={(e) => {
                  setGenName(e.target.value);
                  if (genFieldErrors.name) {
                    setGenFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }
                }}
                placeholder="e.g. Atlantic Frame Productions 2026"
                required
                aria-invalid={Boolean(genFieldErrors.name)}
                aria-describedby={genFieldErrors.name ? 'lp-gen-err-name' : undefined}
              />
              {genFieldErrors.name ? (
                <span id="lp-gen-err-name" className="lp-admin-field-error" role="alert">
                  {genFieldErrors.name}
                </span>
              ) : null}
            </label>
            <div className="lp-admin-grid-2">
              <label className="lp-admin-label">
                Production begins
                <input
                  type="date"
                  className={`lp-admin-input${genFieldErrors.start ? ' is-invalid' : ''}`}
                  value={genStart}
                  onChange={(e) => {
                    setGenStart(e.target.value);
                    if (genFieldErrors.start) {
                      setGenFieldErrors((prev) => ({ ...prev, start: undefined }));
                    }
                  }}
                  required
                  aria-invalid={Boolean(genFieldErrors.start)}
                />
                {genFieldErrors.start ? (
                  <span className="lp-admin-field-error" role="alert">{genFieldErrors.start}</span>
                ) : null}
              </label>
              <label className="lp-admin-label">
                Production ends
                <input
                  type="date"
                  className={`lp-admin-input${genFieldErrors.end ? ' is-invalid' : ''}`}
                  value={genEnd}
                  onChange={(e) => {
                    setGenEnd(e.target.value);
                    if (genFieldErrors.end) {
                      setGenFieldErrors((prev) => ({ ...prev, end: undefined }));
                    }
                  }}
                  required
                  aria-invalid={Boolean(genFieldErrors.end)}
                />
                {genFieldErrors.end ? (
                  <span className="lp-admin-field-error" role="alert">{genFieldErrors.end}</span>
                ) : null}
              </label>
            </div>
            <div className="lp-admin-grid-2">
              <label className="lp-admin-label">
                Production country
                <select
                  className={`lp-admin-input${genFieldErrors.dest ? ' is-invalid' : ''}`}
                  value={genDest}
                  onChange={(e) => {
                    setGenDest(e.target.value);
                    if (genFieldErrors.dest) {
                      setGenFieldErrors((prev) => ({ ...prev, dest: undefined }));
                    }
                  }}
                  required
                  aria-invalid={Boolean(genFieldErrors.dest)}
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                {genFieldErrors.dest ? (
                  <span className="lp-admin-field-error" role="alert">{genFieldErrors.dest}</span>
                ) : null}
              </label>
              <label className="lp-admin-label">
                Coming from
                <select
                  className={`lp-admin-input${genFieldErrors.origin ? ' is-invalid' : ''}`}
                  value={genOrigin}
                  onChange={(e) => {
                    setGenOrigin(e.target.value);
                    if (genFieldErrors.origin) {
                      setGenFieldErrors((prev) => ({ ...prev, origin: undefined }));
                    }
                  }}
                  required
                  aria-invalid={Boolean(genFieldErrors.origin)}
                >
                  {COUNTRY_OPTIONS.map((c) => (
                    <option key={c.code} value={c.code}>{c.label}</option>
                  ))}
                </select>
                {genFieldErrors.origin ? (
                  <span className="lp-admin-field-error" role="alert">{genFieldErrors.origin}</span>
                ) : null}
              </label>
            </div>

            <label className="lp-admin-label">
              Total participants
              <input
                type="number"
                min={1}
                max={200}
                className={`lp-admin-input${genFieldErrors.total ? ' is-invalid' : ''}`}
                value={genTotal}
                onChange={(e) => {
                  setGenTotal(e.target.value);
                  if (genFieldErrors.total) {
                    setGenFieldErrors((prev) => ({ ...prev, total: undefined }));
                  }
                }}
                required
                aria-invalid={Boolean(genFieldErrors.total)}
              />
              {genFieldErrors.total ? (
                <span className="lp-admin-field-error" role="alert">{genFieldErrors.total}</span>
              ) : null}
            </label>

            {genError ? <div className="lp-delete-err" role="alert">{genError}</div> : null}
            {genMessage ? <div className="lp-admin-ok" role="status">{genMessage}</div> : null}

            <div className="lp-modal-actions" style={{ marginTop: 14 }}>
              <button
                type="submit"
                className="lp-bn-btn"
                disabled={generating || !genFormReady}
              >
                {generating ? 'Creating…' : 'Create demo'}
              </button>
            </div>
          </form>

          <div className="lp-admin-packs">
            <div className="lp-admin-subh">Generated files</div>
            {packsLoading ? <div className="lp-admin-muted">Loading generated files…</div> : null}
            {!packsLoading && generatedPacks.length === 0 ? (
              <div className="lp-admin-muted">No generated files yet. Create a demo to see them here.</div>
            ) : null}
            {generatedPacks.map((pack) => (
              <div key={pack.pack_id} className="lp-admin-pack">
                <div>
                  <div className="lp-admin-row-title">{pack.production_name}</div>
                  <div className="lp-admin-row-meta">
                    {[
                      pack.production_country_name || pack.production_country,
                      `${pack.start_date} - ${pack.end_date}`,
                      `${pack.total_participants} people`,
                    ].join(' · ')}
                  </div>
                  <div className="lp-admin-file-chips">
                    {FILE_KINDS.map((file) => (
                      <button
                        key={file.kind}
                        type="button"
                        className="lp-admin-chip"
                        disabled={Boolean(downloadingKey)}
                        onClick={() => downloadGenerated(pack.pack_id, file.kind)}
                      >
                        {downloadingKey === `${pack.pack_id}:${file.kind}` ? '…' : file.label}
                      </button>
                    ))}
                  </div>
                </div>
                <button
                  type="button"
                  className="lp-bn-btn"
                  disabled={Boolean(downloadingKey)}
                  onClick={() => downloadGenerated(pack.pack_id, null)}
                >
                  {downloadingKey === `${pack.pack_id}:zip` ? 'Downloading…' : 'Download all'}
                </button>
              </div>
            ))}
          </div>
        </section>
        ) : null}

        {activeTab === 'productions' ? (
        <section
          className="lp-demo-panel"
          id="lp-demo-panel-productions"
          role="tabpanel"
          aria-labelledby="lp-demo-tab-productions"
        >
          {prodLoading ? (
            <div className="lp-admin-muted">Loading productions…</div>
          ) : null}
          {prodError ? (
            <div className="lp-delete-err" role="alert">
              {prodError}
              <button type="button" className="lp-bn-btn ghost" style={{ marginLeft: 12 }} onClick={loadProductions}>
                Retry
              </button>
            </div>
          ) : null}
          {rosterError ? (
            <div className="lp-delete-err" role="alert">{rosterError}</div>
          ) : null}
          {resetMessage ? (
            <div className="lp-admin-ok" role="status">{resetMessage}</div>
          ) : null}
          {!prodLoading && !prodError && productions.length === 0 ? (
            <div className="lp-admin-muted">No productions in this organization.</div>
          ) : null}
          <div className="lp-demo-cards">
            {productions.map((production) => {
              const destCode = String(production.destination_country || '').trim().toUpperCase();
              const destName = destCode
                ? (countryNameFromIso2(destCode) || destCode)
                : '—';
              const participants = Number(production.participant_count) || 0;
              const demoUsed = productionDemoHasBeenUsed(production);
              return (
                <article key={production.id} className="lp-demo-card">
                  <div className="lp-demo-card-name">{production.name}</div>
                  <div className="lp-demo-card-stat lp-demo-card-stat--country">
                    <small>{t('lpWorkbench.country')}</small>
                    <strong>{destName}</strong>
                  </div>
                  <div className="lp-demo-card-stat lp-demo-card-stat--count">
                    <small>{t('lpWorkbench.participants')}</small>
                    <strong>{participants}</strong>
                  </div>
                  <div className="lp-demo-card-actions">
                    <button
                      type="button"
                      className="lp-bn-btn ghost"
                      onClick={() => navigate(`/org/productions/${production.id}`)}
                    >
                      {t('lpWorkbench.openProduction')}
                    </button>
                    <button
                      type="button"
                      className="lp-bn-btn"
                      onClick={() => openStartDemo(production)}
                    >
                      {demoUsed
                        ? t('lpWorkbench.restartDemoCard')
                        : t('lpWorkbench.startDemo')}
                    </button>
                  </div>
                </article>
              );
            })}
          </div>
        </section>
        ) : null}

        {activeTab === 'contacts' ? (
        <section
          className="lp-admin-panel"
          id="lp-demo-panel-contacts"
          role="tabpanel"
          aria-labelledby="lp-demo-tab-contacts"
        >
          <h2 id="lp-admin-contact-title">Edit email / phone</h2>

          <form className="lp-admin-search" onSubmit={searchUsers}>
            <input
              type="search"
              className="lp-admin-input"
              placeholder="Search users…"
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              aria-label="Search users"
            />
            <button type="submit" className="lp-bn-btn" disabled={userLoading}>
              {userLoading ? 'Searching…' : 'Search'}
            </button>
          </form>

          {userError ? (
            <div className="lp-delete-err" role="alert" style={{ marginTop: 12 }}>
              {userError}
            </div>
          ) : null}

          <div className="lp-admin-list" style={{ marginTop: 14 }}>
            {users.map((user) => (
              <button
                key={user.id}
                type="button"
                className={`lp-admin-row lp-admin-row-btn${selectedUser?.id === user.id ? ' is-selected' : ''}`}
                onClick={() => selectUser(user)}
              >
                <div>
                  <div className="lp-admin-row-title">{user.name || `User #${user.id}`}</div>
                  <div className="lp-admin-row-meta">
                    {[user.email || 'no email', user.phone || 'no phone', user.role]
                      .filter(Boolean)
                      .join(' · ')}
                  </div>
                </div>
                <div className="lp-go">›</div>
              </button>
            ))}
          </div>

          {selectedUser && editingInviteId == null ? renderContactEditor() : null}
        </section>
        ) : null}
      </div>

      {newDemoOpen ? (
        <div
          className="lp-modal-wrap lp-modal-wrap-top lp-demo-start-wrap"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-new-demo-title"
          onClick={closeNewDemo}
        >
          <form
            className="lp-demo-start-modal lp-new-demo-modal"
            translate="no"
            onClick={(e) => e.stopPropagation()}
            onSubmit={submitNewDemo}
            noValidate
          >
            <button
              type="button"
              className="lp-demo-start-close"
              onClick={closeNewDemo}
              disabled={creatingDemo}
              aria-label={t('lpWorkbench.close')}
            >
              ×
            </button>
            <h2 id="lp-new-demo-title">{t('lpWorkbench.newDemo')}</h2>

            <label className="lp-demo-start-field">
              {t('lpWorkbench.productionName')}
              <input
                className={`lp-demo-start-input${genFieldErrors.name ? ' is-invalid' : ''}`}
                type="text"
                value={genName}
                onChange={(e) => {
                  setGenName(e.target.value);
                  if (genFieldErrors.name) {
                    setGenFieldErrors((prev) => ({ ...prev, name: undefined }));
                  }
                }}
                disabled={creatingDemo}
                autoFocus
                autoComplete="off"
                required
                aria-invalid={Boolean(genFieldErrors.name)}
              />
              {genFieldErrors.name ? (
                <span className="lp-admin-field-error" role="alert">{genFieldErrors.name}</span>
              ) : null}
            </label>

            <label className="lp-demo-start-field">
              {t('lpWorkbench.demoCountry')}
              <span className="lp-demo-start-select">
                <select
                  className={`lp-demo-start-input${genFieldErrors.dest ? ' is-invalid' : ''}`}
                  value={genDest}
                  onChange={(e) => {
                    setGenDest(e.target.value);
                    if (genFieldErrors.dest) {
                      setGenFieldErrors((prev) => ({ ...prev, dest: undefined }));
                    }
                  }}
                  disabled={creatingDemo}
                  required
                  aria-invalid={Boolean(genFieldErrors.dest)}
                >
                  {NEW_DEMO_COUNTRY_OPTIONS.map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {opt.label}
                    </option>
                  ))}
                </select>
              </span>
              {genFieldErrors.dest ? (
                <span className="lp-admin-field-error" role="alert">{genFieldErrors.dest}</span>
              ) : null}
            </label>

            {newDemoError ? (
              <div className="lp-delete-err" role="alert">{newDemoError}</div>
            ) : null}

            <div className="lp-demo-start-foot">
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={closeNewDemo}
                disabled={creatingDemo}
              >
                {t('lpWorkbench.cancel')}
              </button>
              <button
                type="submit"
                className="lp-bn-btn"
                disabled={creatingDemo || !newDemoReady}
              >
                {creatingDemo ? t('lpWorkbench.creatingDemo') : t('lpWorkbench.createDemo')}
              </button>
            </div>
          </form>
        </div>
      ) : null}

      {resetTarget ? (
        <div
          className="lp-modal-wrap lp-modal-wrap-top lp-demo-start-wrap"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-admin-reset-title"
          onClick={closeStartDemo}
        >
          <div className="lp-demo-start-modal" translate="no" onClick={(e) => e.stopPropagation()}>
            <button
              type="button"
              className="lp-demo-start-close"
              onClick={closeStartDemo}
              disabled={resetting}
              aria-label={t('lpWorkbench.close')}
            >
              ×
            </button>
            <h2 id="lp-admin-reset-title">
              {t('lpWorkbench.startProductionTitle', { name: resetTarget.name || 'production' })}
            </h2>

            <label className="lp-demo-start-field">
              {t('lpWorkbench.yourName')}
              <input
                className="lp-demo-start-input"
                type="text"
                value={demoHost.name}
                onChange={(e) => updateDemoHost({ name: e.target.value })}
                disabled={resetting}
                autoComplete="name"
              />
            </label>
            <label className="lp-demo-start-field">
              {t('lpWorkbench.yourEmail')}
              <input
                className="lp-demo-start-input"
                type="email"
                value={demoHost.email}
                onChange={(e) => updateDemoHost({ email: e.target.value })}
                disabled={resetting}
                autoComplete="email"
              />
            </label>
            <label className="lp-demo-start-field">
              {t('lpWorkbench.comingFrom')}
              <span className="lp-demo-start-select">
                <select
                  className="lp-demo-start-input"
                  value={demoHost.country || ''}
                  onChange={(e) => updateDemoHost({ country: e.target.value })}
                  disabled={resetting || demoCountriesLoading}
                >
                  {(demoGuestCountries.length ? demoGuestCountries : COUNTRY_OPTIONS).map((opt) => (
                    <option key={opt.code} value={opt.code}>
                      {demoGuestCountries.length
                        ? opt.label
                        : (countryNameFromIso2(opt.code) || opt.label)}
                    </option>
                  ))}
                </select>
              </span>
            </label>

            <div className="lp-demo-start-audience">
              <span>{t('lpWorkbench.audienceEmails')}</span>
              {audienceEmails.map((email, index) => (
                <div key={`audience-${index}`} className="lp-demo-start-audience-row">
                  <input
                    className="lp-demo-start-input"
                    type="email"
                    value={email}
                    onChange={(e) => updateAudienceEmail(index, e.target.value)}
                    disabled={resetting}
                    placeholder="name@example.com"
                    aria-label={`${t('lpWorkbench.audienceEmails')} ${index + 1}`}
                    autoComplete="off"
                  />
                  <button
                    type="button"
                    className="lp-demo-start-audience-remove"
                    onClick={() => removeAudienceEmail(index)}
                    disabled={resetting}
                    aria-label={t('lpWorkbench.removeAudienceEmail')}
                  >
                    ×
                  </button>
                </div>
              ))}
              <button
                type="button"
                className="lp-demo-start-add"
                onClick={() => setAudienceEmails((prev) => [...prev, ''])}
                disabled={resetting}
              >
                {t('lpWorkbench.addAudienceEmail')}
              </button>
            </div>

            <div className="lp-demo-start-join">
              <span>
                <small>{t('lpWorkbench.joinLinkForRun')}</small>
                {demoJoinUrl ? (
                  <strong className="lp-demo-start-link-text">
                    {joinLinkDisplay(demoJoinUrl)}
                  </strong>
                ) : (
                  <strong className="lp-demo-start-link-text lp-demo-start-link-text--muted">
                    {t('lpWorkbench.noJoinLinkYet')}
                  </strong>
                )}
              </span>
              <button
                type="button"
                className="lp-demo-start-copy"
                onClick={copyDemoJoinLink}
                disabled={!demoJoinUrl || resetting}
              >
                {demoJoinCopied ? t('lpWorkbench.joinLinkCopied') : t('lpWorkbench.copyJoinLink')}
              </button>
            </div>

            {resetError ? (
              <div className="lp-delete-err" role="alert">{resetError}</div>
            ) : null}

            <div className="lp-demo-start-foot">
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={closeStartDemo}
                disabled={resetting}
              >
                {t('lpWorkbench.cancel')}
              </button>
              <button
                type="button"
                className="lp-bn-btn lp-demo-start-restart"
                onClick={confirmResetProduction}
                disabled={resetting}
              >
                {resetting
                  ? t('lpWorkbench.startingDemo')
                  : productionDemoHasBeenUsed(resetTarget)
                    ? t('lpWorkbench.restartDemo')
                    : t('lpWorkbench.startDemoAction')}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteTarget ? (
        <div
          className="lp-modal-wrap lp-modal-wrap-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-admin-delete-title"
          onClick={() => !deleting && setDeleteTarget(null)}
        >
          <div className="lp-modal lp-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lp-modal-h">
              <span id="lp-admin-delete-title">Delete production</span>
              <button
                type="button"
                className="x"
                onClick={() => !deleting && setDeleteTarget(null)}
                disabled={deleting}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="lp-modal-sub">
              Delete <b>{deleteTarget.name}</b>? This permanently removes the production,
              its crew list, and participant accounts/data. This cannot be undone.
            </div>
            {deleteError ? (
              <div className="lp-delete-err" role="alert">{deleteError}</div>
            ) : null}
            <div className="lp-modal-actions">
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={() => setDeleteTarget(null)}
                disabled={deleting}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lp-bn-btn lp-bn-btn-danger"
                onClick={confirmDeleteProduction}
                disabled={deleting}
              >
                {deleting ? 'Deleting…' : 'Delete permanently'}
              </button>
            </div>
          </div>
        </div>
      ) : null}

      {deleteMemberTarget ? (
        <div
          className="lp-modal-wrap lp-modal-wrap-top"
          role="dialog"
          aria-modal="true"
          aria-labelledby="lp-admin-delete-member-title"
          onClick={() => !deletingMember && setDeleteMemberTarget(null)}
        >
          <div className="lp-modal lp-modal-sm" onClick={(e) => e.stopPropagation()}>
            <div className="lp-modal-h">
              <span id="lp-admin-delete-member-title">Remove member</span>
              <button
                type="button"
                className="x"
                onClick={() => !deletingMember && setDeleteMemberTarget(null)}
                disabled={deletingMember}
                aria-label="Close"
              >
                &times;
              </button>
            </div>
            <div className="lp-modal-sub">
              Remove{' '}
              <b>
                {deleteMemberTarget.person.name
                  || deleteMemberTarget.person.email
                  || `participant #${deleteMemberTarget.person.id}`}
              </b>{' '}
              from <b>{deleteMemberTarget.production.name}</b> only? Their invite and this
              production&apos;s user account are deleted. Other productions are not affected.
            </div>
            {deleteMemberError ? (
              <div className="lp-delete-err" role="alert">{deleteMemberError}</div>
            ) : null}
            <div className="lp-modal-actions">
              <button
                type="button"
                className="lp-bn-btn ghost"
                onClick={() => setDeleteMemberTarget(null)}
                disabled={deletingMember}
              >
                Cancel
              </button>
              <button
                type="button"
                className="lp-bn-btn lp-bn-btn-danger"
                onClick={confirmDeleteMember}
                disabled={deletingMember}
              >
                {deletingMember ? 'Removing…' : 'Remove member'}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </LpWorkbenchShell>
  );
}
